import type { PropertyCommunicationField, PropertyCommunicationPlaceholder } from '@/shared/api';
import { KNOWN_FIELDS, PORTAL_LINK, segments, suggestion } from './tokenAnalysis';

export interface FieldRules {
  placeholders: PropertyCommunicationPlaceholder[];
  /** `null` beim Linktext: dort sind gar keine Felder erlaubt. */
  field: PropertyCommunicationField | null;
  max: number;
  singleLine: boolean;
}

const number = (value: number) => value.toLocaleString('de-DE');

/**
 * Was der Server an diesem Text ablehnen würde, als Sätze — dieselben Regeln, damit ein Fehler am
 * Feld steht, statt nach dem Speichern als „ungültig" ohne Ort zurückzukommen.
 */
export function fieldIssues(value: string | null | undefined, rules: FieldRules): string[] {
  if (!value?.trim()) return [];
  const issues: string[] = [];
  const parts = segments(value, rules.placeholders, rules.field);
  const unknown = parts.find((part) => part.kind === 'unknown');
  if (unknown) {
    const guess = suggestion(unknown.text);
    issues.push(
      `„${unknown.text}“ gibt es nicht.` +
        (guess
          ? ` Gemeint ist wohl „${guess}“ – über „Feld einfügen“ wählen.`
          : ' Wählen Sie ein Feld über „Feld einfügen“.'),
    );
  }
  const misplaced = parts.find((part) => part.kind === 'misplaced');
  if (misplaced) issues.push(misplacedMessage(misplaced.text, rules));
  if (parts.filter((part) => part.kind === 'link').length > 1) {
    issues.push('Der Gastportal-Link darf nur einmal vorkommen.');
  }
  if (parts.some((part) => part.kind === 'stray')) {
    issues.push('Ein Feld ist unvollständig – es beginnt mit „{{“ und endet mit „}}“.');
  }
  if (/[<>]/.test(value)) issues.push('Spitze Klammern < > sind nicht erlaubt.');
  const normalized = value.replace(/\r\n?/g, '\n').trim();
  if (rules.singleLine && normalized.includes('\n')) issues.push('Dieses Feld hat nur eine Zeile.');
  if (normalized.length > rules.max) {
    issues.push(
      `Höchstens ${number(rules.max)} Zeichen – ${number(normalized.length - rules.max)} zu viel.`,
    );
  }
  return issues;
}

function misplacedMessage(token: string, rules: FieldRules) {
  const known = KNOWN_FIELDS[token];
  const label = known?.label ?? token;
  if (!rules.field) return `Im Linktext sind keine Felder erlaubt – bitte „${label}“ entfernen.`;
  const offered = rules.placeholders.some((item) => item.token === token);
  if (token === PORTAL_LINK && offered) {
    return `„${label}“ ist hier nicht erlaubt. Der Gastportal-Link gehört in den Text – genau einmal.`;
  }
  return `„${label}“ ist hier nicht erlaubt.${known?.why ? ` ${known.why}` : ''}`;
}
